/**
 * Respect Service
 * 
 * Handles Firestore operations for Respect and Mutual Respect relations.
 * 
 * GEEN:
 * - Respect tellen
 * - Respect rangschikken
 * - Posts sorteren op "meest"
 * - Engagement scores berekenen
 * - Analytics events toevoegen
 * - Notifications sturen
 * - Aanbevelingen doen
 * - "Trending" logica bouwen
 * - "Populair" labels gebruiken
 * - Gebruikers vergelijken
 * - Impliciete voorkeuren tonen
 */

import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { 
  RespectRelation, 
  MutualRespectRelation, 
  Responder, 
  MutualRespectPair,
  PostRespectState 
} from '../types/respect';

const RESPECT_COLLECTION = 'respectRelations';
const MUTUAL_RESPECT_COLLECTION = 'mutualRespectRelations';

/**
 * Haalt alle Respect-relaties voor één post
 * Retourneert lijst van responderId waarden
 */
export const getRespectsForPost = async (postId: string): Promise<Responder[]> => {
  const q = query(
    collection(db, RESPECT_COLLECTION),
    where('postId', '==', postId)
  );
  
  const snapshot = await getDocs(q);
  const responders: Responder[] = [];
  
  for (const docSnapshot of snapshot.docs) {
    const data = docSnapshot.data();
    const responderId = data.responderId;
    
    // Haal user profile op voor display
    const userProfileDoc = await getDoc(doc(db, 'userProfiles', responderId));
    if (userProfileDoc.exists()) {
      const userData = userProfileDoc.data();
      responders.push({
        id: responderId,
        displayName: userData.displayName || 'Gebruiker',
        photoURL: userData.photoURL || ''
      });
    }
  }
  
  return responders;
};

/**
 * Haalt alle Mutual Respect paren voor één post
 * Retourneert lijst van {userA, userB} paren
 */
export const getMutualRespectsForPost = async (postId: string): Promise<MutualRespectPair[]> => {
  const q = query(
    collection(db, MUTUAL_RESPECT_COLLECTION),
    where('postId', '==', postId)
  );
  
  const snapshot = await getDocs(q);
  const pairs: MutualRespectPair[] = [];
  
  for (const docSnapshot of snapshot.docs) {
    const data = docSnapshot.data();
    
    // Haal user profiles op
    const userADoc = await getDoc(doc(db, 'userProfiles', data.userA));
    const userBDoc = await getDoc(doc(db, 'userProfiles', data.userB));
    
    const userA: Responder = {
      id: data.userA,
      displayName: userADoc.exists() ? userADoc.data().displayName || 'Gebruiker' : 'Gebruiker',
      photoURL: userADoc.exists() ? userADoc.data().photoURL || '' : ''
    };
    
    const userB: Responder = {
      id: data.userB,
      displayName: userBDoc.exists() ? userBDoc.data().displayName || 'Gebruiker' : 'Gebruiker',
      photoURL: userBDoc.exists() ? userBDoc.data().photoURL || '' : ''
    };
    
    pairs.push({ userA, userB });
  }
  
  return pairs;
};

/**
 * Checkt of een gebruiker al heeft gereageerd op een post
 * Retourneert boolean
 */
export const hasUserRespectedPost = async (userId: string, postId: string): Promise<boolean> => {
  const q = query(
    collection(db, RESPECT_COLLECTION),
    where('postId', '==', postId),
    where('responderId', '==', userId)
  );
  
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

/**
 * Maakt een RespectRelation aan wanneer een gebruiker inhoudelijk reageert
 * Wordt ALLEEN aangeroepen vanuit comment/reply functionaliteit
 */
export const createRespectRelation = async (
  postId: string,
  postAuthorId: string,
  responderId: string
): Promise<string> => {
  // Check of relatie al bestaat
  const exists = await hasUserRespectedPost(responderId, postId);
  if (exists) {
    return ''; // Geen duplicaat maken
  }
  
  const docRef = await addDoc(collection(db, RESPECT_COLLECTION), {
    postId,
    postAuthorId,
    responderId,
    createdAt: serverTimestamp()
  });
  
  return docRef.id;
};

/**
 * Checkt of er een Mutual Respect relatie bestaat tussen twee gebruikers voor een post
 */
export const hasMutualRespect = async (
  postId: string,
  userA: string,
  userB: string
): Promise<boolean> => {
  // Check beide richtingen
  const q1 = query(
    collection(db, MUTUAL_RESPECT_COLLECTION),
    where('postId', '==', postId),
    where('userA', '==', userA),
    where('userB', '==', userB)
  );
  
  const q2 = query(
    collection(db, MUTUAL_RESPECT_COLLECTION),
    where('postId', '==', postId),
    where('userA', '==', userB),
    where('userB', '==', userA)
  );
  
  const [snapshot1, snapshot2] = await Promise.all([
    getDocs(q1),
    getDocs(q2)
  ]);
  
  return !snapshot1.empty || !snapshot2.empty;
};

/**
 * Maakt een MutualRespectRelation aan wanneer post-auteur terug reageert
 * Wordt ALLEEN aangeroepen wanneer auteur reageert op iemand die eerder reageerde
 */
export const createMutualRespectRelation = async (
  postId: string,
  userA: string,
  userB: string
): Promise<string> => {
  // Check of mutual respect al bestaat
  const exists = await hasMutualRespect(postId, userA, userB);
  if (exists) {
    return ''; // Geen duplicaat maken
  }
  
  const docRef = await addDoc(collection(db, MUTUAL_RESPECT_COLLECTION), {
    postId,
    userA,
    userB,
    createdAt: serverTimestamp()
  });
  
  return docRef.id;
};

/**
 * Berekent de afgeleide state voor een post
 * Deze waarden worden NOOIT opgeslagen
 */
export const getPostRespectState = async (
  postId: string,
  postCreatedAt: Date
): Promise<PostRespectState> => {
  const responders = await getRespectsForPost(postId);
  const mutualPairs = await getMutualRespectsForPost(postId);
  
  // isRecent: post binnen 24 uur geplaatst
  const now = new Date();
  const hoursDiff = (now.getTime() - postCreatedAt.getTime()) / (1000 * 60 * 60);
  const isRecent = hoursDiff <= 24;
  
  return {
    hasInteraction: responders.length > 0,
    hasReply: responders.length > 0, // Respect ontstaat door reply
    hasMutualRespect: mutualPairs.length > 0,
    isRecent
  };
};

/**
 * Berekent basis zichtbaarheidstijd in uren
 * 
 * Baseline: 24 uur
 * hasInteraction → +12 uur
 * hasMutualRespect → +24 uur (kan opnieuw verschijnen)
 * 
 * GEEN ranking, GEEN sortering op "meest"
 * Alleen tijdsverlenging
 */
export const calculateVisibilityHours = (state: PostRespectState): number => {
  let hours = 24; // Baseline
  
  if (state.hasInteraction) {
    hours += 12;
  }
  
  if (state.hasMutualRespect) {
    hours += 24;
  }
  
  return hours;
};

/**
 * Post interface voor zichtbaarheidsfiltering
 */
interface VisiblePost {
  id: string;
  authorId: string;
  content: string;
  createdAt: Date;
  [key: string]: any;
}

/**
 * Haalt zichtbare posts op basis van tijdsfilter
 * 
 * visibleUntil = createdAt + calculateVisibilityHours(state)
 * Filter: visibleUntil > now
 * Sortering: ALLEEN op createdAt (descending)
 * 
 * GEEN ranking op interactie
 * GEEN boost
 * GEEN secondary sort
 */
export const getVisiblePosts = async (): Promise<VisiblePost[]> => {
  const now = new Date();
  
  // Haal alle posts op, gesorteerd op createdAt (ENIGE sortering)
  const postsQuery = query(
    collection(db, 'communityPosts'),
    orderBy('createdAt', 'desc')
  );
  
  const snapshot = await getDocs(postsQuery);
  const visiblePosts: VisiblePost[] = [];
  
  for (const docSnapshot of snapshot.docs) {
    const data = docSnapshot.data();
    const postId = docSnapshot.id;
    
    // Converteer createdAt naar Date
    const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
    
    // Bereken respect state voor deze post
    const state = await getPostRespectState(postId, createdAt);
    
    // Bereken visibleUntil
    const visibilityHours = calculateVisibilityHours(state);
    
    // Check voor extended visibility boost
    const boosts = data.visibilityBoosts as any;
    let visibilityMultiplier = 1.0;
    if (boosts?.extendedVisibility) {
      const until = boosts.extendedVisibility.until?.toDate?.() || new Date(boosts.extendedVisibility.until);
      if (until > now) {
        visibilityMultiplier = boosts.extendedVisibility.multiplier || 2.0;
      }
    }
    
    const visibleUntil = new Date(createdAt.getTime() + (visibilityHours * visibilityMultiplier * 60 * 60 * 1000));
    
    // Filter: alleen posts waar visibleUntil > now
    if (visibleUntil > now) {
      visiblePosts.push({
        id: postId,
        authorId: data.authorId || data.userId,
        content: data.content,
        createdAt,
        ...data
      });
    }
  }
  
  // Geen hersortering - volgorde blijft createdAt desc
  return visiblePosts;
};

export default {
  getRespectsForPost,
  getMutualRespectsForPost,
  hasUserRespectedPost,
  createRespectRelation,
  hasMutualRespect,
  createMutualRespectRelation,
  getPostRespectState,
  calculateVisibilityHours,
  getVisiblePosts
};
